import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";

const CANONICAL = "https://bauerip.com/services/foreign-associate-for-us-firms";

const faqs = [
  {
    q: "Do you take instructions from US firms as foreign associate?",
    a: "Yes. US patent and trademark firms instruct me directly for EPO and EUIPO work. The client relationship stays with your firm; I act as your European associate and report to you, not to your client, unless you ask otherwise.",
  },
  {
    q: "Will you solicit our clients?",
    a: "No. Instructions from US firms come with a non-solicitation undertaking in writing: I do not approach your client for direct work, and any inbound contact from them is referred back to you.",
  },
  {
    q: "How do you handle conflicts?",
    a: "Every new instruction is conflict-checked against existing parties and technical fields before I accept it. If a conflict exists, I tell you the same day and decline — no work is started and no confidential material is retained.",
  },
  {
    q: "Which deadlines do you docket?",
    a: "I docket all EPO and EUIPO deadlines on the matters you instruct me on, including regional phase entry, response terms, renewal dates and opposition windows. I send reminders in advance and confirm every filing with the official receipt.",
  },
  {
    q: "How do you report and bill?",
    a: "Reporting letters follow your preferred format — plain email summary, formal reporting letter, or structured data for your docketing system. Invoices are itemised per matter in EUR with official fees separated from professional fees, so you can pass them through cleanly.",
  },
  {
    q: "Can you also handle EU trade marks for our firm?",
    a: "Yes. I am both a European Patent Attorney and a European Trade Mark Attorney, so patent and EUTM instructions from the same US firm can run through one associate rather than two.",
  },
];

export const Route = createFileRoute("/services/foreign-associate-for-us-firms")({
  head: () => ({
    meta: [
      { title: "EPO Representative for US Counsel | European Foreign Associate" },
      {
        name: "description",
        content:
          "European foreign associate for US patent firms: 50+ EPO prosecution matters, direct technical analysis, conflict checks, docketing and clear reporting.",
      },
      { property: "og:title", content: "EPO Representative for US Counsel | European Foreign Associate" },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "An EQE-qualified European Patent Attorney handling claim analysis and EPO responses directly for US firms, backed by 50+ prosecution matters.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", hrefLang: "en-US", href: CANONICAL },
      { rel: "alternate", hrefLang: "en", href: CANONICAL },
      { rel: "alternate", hrefLang: "x-default", href: CANONICAL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Bauer IP — European foreign associate for US firms",
          url: CANONICAL,
          areaServed: ["United States", "EP", "EU", "UPC"],
          provider: {
            "@type": "Person",
            name: "Karel Bauer",
            jobTitle: "European Patent Attorney and European Trade Mark Attorney",
          },
          serviceType: [
            "Foreign associate services",
            "EPO instruction handling",
            "EUIPO instruction handling",
            "European docketing and reporting",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ForeignAssociatePage,
});

const epoWork = [
  {
    t: "PCT regional phase entry",
    d: "Entry within the 31-month term, claim amendment on entry, and a short note on what European examination will do to the US-drafted set.",
  },
  {
    t: "Examination reports",
    d: "Article 94(3) responses, Rule 71(3) handling, auxiliary requests and oral proceedings before the examining division.",
  },
  {
    t: "Opposition and appeal",
    d: "Notices of opposition within the 9-month window, defence of your client's granted EP, and representation before the boards of appeal.",
  },
  {
    t: "Validation and Unitary Patent",
    d: "Post-grant validation across national offices or a unitary effect request, with a country-by-country cost table before you instruct.",
  },
  {
    t: "UPC matters",
    d: "Opt-out and opt-in filings per family, plus representation before the Unified Patent Court as your firm's European representative.",
  },
  {
    t: "EUIPO trade marks",
    d: "EUTM applications, oppositions, refusals on absolute or relative grounds, renewals, assignments and representation for non-EU applicants.",
  },
];

const terms = [
  {
    t: "Conflict check before acceptance",
    d: "Parties and technical field checked against existing matters the same day. A conflict means a clear decline, not a delayed answer.",
  },
  {
    t: "Non-solicitation in writing",
    d: "Your client stays your client. I do not market direct services to firms instructed through you, and inbound approaches are referred back.",
  },
  {
    t: "You keep the client relationship",
    d: "All reporting goes to your firm by default. I join client calls only when you ask me to, and under your firm's name where you prefer it.",
  },
  {
    t: "Confidentiality on your paper",
    d: "Happy to work under your firm's engagement or NDA terms rather than imposing mine, provided EPO professional secrecy rules are met.",
  },
];

const docketing = [
  {
    t: "Deadlines docketed on receipt",
    d: "Every instruction is docketed the day it arrives — regional phase, response terms, renewal dates, opposition and appeal windows.",
  },
  {
    t: "Two reminders, then confirmation",
    d: "A first reminder well ahead of the term, a second close to it, and a filing confirmation with the official EPO or EUIPO receipt attached.",
  },
  {
    t: "Reporting in your format",
    d: "Short email summary, formal reporting letter, or a structured field list your docketing team can key in directly. Tell me which and it stays consistent.",
  },
  {
    t: "Itemised EUR invoices",
    d: "Official fees and professional fees listed separately per matter, so charges pass through to your client without reconstruction.",
  },
];

function ForeignAssociatePage() {
  const body =
    "Hello Karel,\n\nOur firm is looking for a European associate for the following matter.\n\n— Firm:\n— Contact:\n— Matter type (EP filing, examination report, opposition, EUTM):\n— Application / publication number:\n— Deadline:\n— Notes:\n";

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 pt-20 md:pt-28 pb-16 md:pb-20">
            <p className="eyebrow mb-6">For US patent &amp; trademark firms</p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
              <span className="block text-base font-sans not-italic uppercase tracking-[0.2em] text-muted-foreground mb-4">EPO representative for US counsel</span>A European{" "}
              <span className="italic text-accent-text">foreign associate</span> your
              firm can instruct directly.
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
              More than 50 EPO prosecution matters over 10+ years. Your US firm
              communicates directly with the European Patent Attorney who
              analyses the claims, prepares the response and reports the
              reasoning — with conflict checks, docketing and non-solicitation
              built into the instruction.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton
                variant="accent"
                href={mailto({ subject: "Foreign associate instruction", body })}
                trackLabel="foreign_associate_hero_instruct"
              >
                Send an Instruction
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "Foreign associate rates and terms",
                  body: "Hello Karel,\n\nPlease send your associate rate card and standard terms for US firms.\n\n— Firm:\n— Volume / typical matters:\n",
                })}
                trackLabel="foreign_associate_hero_rates"
              >
                Review Qualifications &amp; Terms
              </CTAButton>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Reply within one business day · US business-hour calls
            </p>
          </div>
        </section>

        <Section
          eyebrow="Prosecution credentials"
          title="The attorney receiving the instruction prepares the EPO argument."
          intro="Karel Bauer has handled more than 50 EPO prosecution matters over 10+ years, with a technical focus in physics, electronics and ICT. He qualified through the EQE and does not delegate the substantive analysis to a trainee."
        >
          <div className="grid gap-px md:grid-cols-3 bg-border border border-border">
            {[
              ["50+", "EPO prosecution matters"],
              ["10+ years", "European patent practice"],
              ["EQE", "European Patent Attorney qualification"],
            ].map(([value, label]) => (
              <div key={label} className="bg-background p-8">
                <p className="font-serif text-3xl text-accent-text">{value}</p>
                <p className="mt-3 text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section
          eyebrow="Instruction handling"
          title="What US firms instruct me on before the EPO and EUIPO."
        >
          <div className="grid gap-px md:grid-cols-2 bg-border">
            {epoWork.map((s) => (
              <div key={s.t} className="bg-background p-8">
                <h3 className="font-serif text-xl text-foreground leading-snug">{s.t}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </Section>

        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Co-counsel terms"
            title="Conflict-free, and your client stays yours."
            intro="The reason firms hesitate to instruct a small European practice is rarely competence — it is the worry that the associate becomes a competitor. These terms are part of every instruction."
          >
            <div className="grid gap-px md:grid-cols-2 bg-border">
              {terms.map((s) => (
                <div key={s.t} className="bg-background p-8">
                  <h3 className="font-serif text-xl text-foreground leading-snug">{s.t}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <Section
          eyebrow="Docketing & reporting"
          title="How deadlines and reports reach your system."
        >
          <ul className="grid gap-px md:grid-cols-2 bg-border">
            {docketing.map((s, i) => (
              <li key={s.t} className="bg-background p-8 flex gap-5">
                <span className="font-serif text-accent-text text-xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>
                  <span className="block font-serif text-xl text-foreground leading-snug">
                    {s.t}
                  </span>
                  <span className="mt-3 block text-muted-foreground leading-relaxed">
                    {s.d}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <div className="border-y border-border bg-primary text-primary-foreground">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24 grid gap-12 md:grid-cols-[1fr_1.4fr]">
            <div>
              <p className="eyebrow mb-4" style={{ color: "var(--accent)" }}>
                Credentials
              </p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                One associate, both registers.
              </h2>
            </div>
            <ul className="grid gap-3 text-base text-primary-foreground/90">
              {[
                "European Patent Attorney — entitled to represent before the EPO",
                "European Trade Mark Attorney — EUIPO representation for non-EU applicants",
                "50+ EPO prosecution matters over more than 10 years",
                "Physics, electronics and ICT technical focus",
                "UPC representative — CEIPI Patent Litigation diploma and ERA/Maastricht certificate",
                "Your instruction is handled by me, not delegated to a trainee",
              ].map((item) => (
                <li key={item} className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border bg-secondary/30">
          <Section eyebrow="FAQ" title="Questions US firms ask before the first instruction.">
            <dl className="divide-y divide-border border-y border-border">
              {faqs.map((f) => (
                <div key={f.q} className="grid md:grid-cols-[1fr_2fr] gap-6 py-8">
                  <dt className="font-serif text-lg text-foreground">{f.q}</dt>
                  <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </Section>
        </div>

        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <h2 className="font-serif text-3xl md:text-5xl text-foreground leading-tight max-w-3xl">
              Send a first matter and see how it is handled.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              A regional phase entry or a single examination report is enough to test the
              reporting, the docketing and the invoice format before you move volume.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton
                variant="accent"
                href={mailto({ subject: "Foreign associate instruction", body })}
                trackLabel="foreign_associate_final_instruct"
              >
                Send an Instruction
              </CTAButton>
            </div>
            <p className="mt-8 text-sm text-muted-foreground">
              Working on your own client's European filing instead?{" "}
              <Link
                to="/services/european-patent-attorney"
                className="underline hover:text-accent-text"
              >
                See the European Patent Attorney services
              </Link>{" "}
              or the{" "}
              <Link
                to="/services/european-trademark-attorney"
                className="underline hover:text-accent-text"
              >
                European Trade Mark Attorney services
              </Link>
              .
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
